import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@repo/shared";
import { useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { T } from "./two-factor-setup.i18n";

type TwoFactorSetupProps = {
  twoFactorEnabled: boolean;
  phoneNumber?: string | null;
};

export function TwoFactorSetup({
  twoFactorEnabled,
  phoneNumber: _phoneNumber,
}: TwoFactorSetupProps) {
  const t = useT(T);
  const queryClient = useQueryClient();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Enable 2FA states
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [enableStep, setEnableStep] = useState<"password" | "qrcode" | "verify">("password");
  const [password, setPassword] = useState("");
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verifyCode, setVerifyCode] = useState("");

  // Change password states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Disable 2FA states
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError(t.fillAllFields);
      return;
    }

    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setError(t.passwordMinLength);
      return;
    }

    if (newPassword.length > PASSWORD_MAX_LENGTH) {
      setError(t.passwordMaxLength);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t.passwordsDoNotMatch);
      return;
    }

    startTransition(async () => {
      const result = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: false,
      });

      if (result.error) {
        setError(result.error.message || t.failedToChangePassword);
        return;
      }

      setShowChangePassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess(t.passwordChanged);
    });
  };

  const handleEnable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError(t.pleaseEnterPassword);
      return;
    }

    startTransition(async () => {
      try {
        const result = await authClient.twoFactor.enable({
          password,
          method: "totp",
        });

        if (result.error) {
          setError(result.error.message || t.failedToEnable);
          return;
        }

        if (result.data?.method === "totp") {
          setTotpUri(result.data.totpURI);
          setBackupCodes(result.data.backupCodes || []);
          setEnableStep("qrcode");
        }
      } catch (err) {
        log.error({ err }, "failed to enable 2FA");
        setError(t.somethingWentWrong);
      }
    });
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!verifyCode || verifyCode.length !== 6) {
      setError(t.pleaseEnterSixDigit);
      return;
    }

    startTransition(async () => {
      try {
        const result = await authClient.twoFactor.verifyTotp({
          code: verifyCode,
        });

        if (result.error) {
          setError(result.error.message || t.invalidCode);
          return;
        }

        setEnableStep("verify");
        setSuccess(t.twoFactorEnabledSuccess);
        queryClient.invalidateQueries({ queryKey: ["profile"] });
      } catch (err) {
        log.error({ err }, "failed to verify TOTP code");
        setError(t.somethingWentWrong);
      }
    });
  };

  const handleDisable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!disablePassword) {
      setError(t.pleaseEnterPassword);
      return;
    }

    startTransition(async () => {
      try {
        const result = await authClient.twoFactor.disable({
          password: disablePassword,
        });

        if (result.error) {
          setError(result.error.message || t.failedToDisable);
          return;
        }

        setShowDisableDialog(false);
        setDisablePassword("");
        setSuccess(t.twoFactorDisabledSuccess);
        queryClient.invalidateQueries({ queryKey: ["profile"] });
      } catch (err) {
        log.error({ err }, "failed to disable 2FA");
        setError(t.somethingWentWrong);
      }
    });
  };

  const resetChangePasswordDialog = () => {
    setShowChangePassword(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
  };

  const resetDisableDialog = () => {
    setShowDisableDialog(false);
    setDisablePassword("");
    setError(null);
  };

  const resetEnableDialog = () => {
    setShowEnableDialog(false);
    setEnableStep("password");
    setPassword("");
    setTotpUri(null);
    setBackupCodes([]);
    setVerifyCode("");
    setError(null);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t.passwordAndAuth}</CardTitle>
          <CardDescription>{t.passwordAndAuthDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {success && (
            <div className="text-sm rounded-md border border-success/40 bg-success/10 p-3">
              {success}
            </div>
          )}

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <h3 className="font-medium">{t.password}</h3>
              <p className="text-sm text-muted-foreground">{t.changePasswordDescription}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setError(null);
                setSuccess(null);
                setShowChangePassword(true);
              }}
            >
              {t.changePassword}
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <h3 className="font-medium">{t.authenticatorApp}</h3>
              <p className="text-sm text-muted-foreground">{t.authenticatorDescription}</p>
            </div>
            <div className="flex items-center gap-2">
              {twoFactorEnabled ? (
                <>
                  <span className="text-sm text-success font-medium">{t.enabled}</span>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setError(null);
                      setSuccess(null);
                      setShowDisableDialog(true);
                    }}
                  >
                    {t.disable}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    setError(null);
                    setSuccess(null);
                    setShowEnableDialog(true);
                  }}
                >
                  {t.enable}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enable 2FA Dialog */}
      <Dialog open={showEnableDialog} onOpenChange={resetEnableDialog}>
        {/* `success` is only set once the change has gone through — 2FA
            enabled, disabled, password changed. Nothing left to discard. */}
        <DialogContent className="sm:max-w-md" guardDismiss={!success}>
          <DialogHeader>
            <DialogTitle>
              {enableStep === "password" && t.enableTitle}
              {enableStep === "qrcode" && t.scanQrCode}
              {enableStep === "verify" && t.twoFaEnabled}
            </DialogTitle>
            <DialogDescription>
              {enableStep === "password" && t.enterPasswordToBegin}
              {enableStep === "qrcode" && t.scanQrCodeDescription}
              {enableStep === "verify" && t.accountProtected}
            </DialogDescription>
          </DialogHeader>

          {enableStep === "password" && (
            <form onSubmit={handleEnable2FA}>
              <div className="space-y-4 py-4">
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password">{t.password}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.enterPassword}
                    autoFocus
                    disabled={isPending}
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogCancel>{t.cancel}</DialogCancel>
                <Button type="submit" loading={isPending}>
                  {t.continue}
                </Button>
              </DialogFooter>
            </form>
          )}

          {enableStep === "qrcode" && (
            <form onSubmit={handleVerifyTotp}>
              <div className="space-y-4 py-4">
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                    {error}
                  </div>
                )}

                {totpUri && (
                  <>
                    <div className="flex justify-center p-4 bg-white rounded-lg">
                      <QRCodeSVG value={totpUri} size={200} />
                    </div>
                    <div className="space-y-2">
                      <Label>{t.manualEntryCode}</Label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-muted rounded-md font-mono text-sm break-all">
                          {new URL(totpUri).searchParams.get("secret")}
                        </code>
                        <Button
                          data-dialog-guard-ignore
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const secret = new URL(totpUri).searchParams.get("secret");
                            if (secret) navigator.clipboard.writeText(secret);
                          }}
                        >
                          {t.copy}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">{t.cantScanQr}</p>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="verifyCode">{t.verificationCode}</Label>
                  <Input
                    id="verifyCode"
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder={t.enterSixDigitCode}
                    maxLength={6}
                    autoComplete="one-time-code"
                    disabled={isPending}
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogCancel>{t.cancel}</DialogCancel>
                <Button type="submit" disabled={verifyCode.length !== 6} loading={isPending}>
                  {t.verifyAndEnable}
                </Button>
              </DialogFooter>
            </form>
          )}

          {enableStep === "verify" && (
            <div className="space-y-4 py-4">
              <div className="text-sm rounded-md border border-success/40 bg-success/10 p-3">
                {t.twoFactorEnabledSuccess}
              </div>

              {backupCodes.length > 0 && (
                <div className="space-y-2">
                  <Label>{t.backupCodes}</Label>
                  <p className="text-sm text-muted-foreground">{t.backupCodesDescription}</p>
                  <div className="grid grid-cols-2 gap-2 p-3 bg-muted rounded-md font-mono text-sm">
                    {backupCodes.map((code) => (
                      <div key={code}>{code}</div>
                    ))}
                  </div>
                  <Button
                    data-dialog-guard-ignore
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(backupCodes.join("\n"));
                    }}
                  >
                    {t.copyCodes}
                  </Button>
                </div>
              )}

              <DialogFooter>
                <Button onClick={resetEnableDialog}>{t.done}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={resetChangePasswordDialog}>
        {/* `success` is only set once the change has gone through — 2FA
            enabled, disabled, password changed. Nothing left to discard. */}
        <DialogContent className="sm:max-w-md" guardDismiss={!success}>
          <DialogHeader>
            <DialogTitle>{t.changePassword}</DialogTitle>
            <DialogDescription>{t.changePasswordDialogDescription}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleChangePassword}>
            <div className="space-y-4 py-4">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="currentPassword">{t.currentPassword}</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder={t.currentPasswordPlaceholder}
                  disabled={isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">{t.newPassword}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t.newPasswordPlaceholder}
                  disabled={isPending}
                />
                <p className="text-xs text-muted-foreground">{t.newPasswordHint}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t.confirmNewPassword}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t.confirmPasswordPlaceholder}
                  disabled={isPending}
                />
              </div>
            </div>
            <DialogFooter>
              <DialogCancel>{t.cancel}</DialogCancel>
              <Button type="submit" loading={isPending}>
                {t.changePassword}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={resetDisableDialog}>
        {/* `success` is only set once the change has gone through — 2FA
            enabled, disabled, password changed. Nothing left to discard. */}
        <DialogContent className="sm:max-w-md" guardDismiss={!success}>
          <DialogHeader>
            <DialogTitle>{t.disableTitle}</DialogTitle>
            <DialogDescription>{t.disableDescription}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleDisable2FA}>
            <div className="space-y-4 py-4">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="disablePassword">{t.password}</Label>
                <Input
                  id="disablePassword"
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder={t.enterPassword}
                  autoFocus
                  disabled={isPending}
                />
              </div>
            </div>
            <DialogFooter>
              <DialogCancel>{t.cancel}</DialogCancel>
              <Button type="submit" variant="destructive" loading={isPending}>
                {t.disable2Fa}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
